import React, { useState } from 'react';
import axios from 'axios';
import './FashionRecommendation.css';

const API_URL = 'https://api.hauteuar.com/fashion-api'; // Replace with your actual API URL in production

const FashionRecommendation = () => {
  const [file, setFile] = useState(null);
  const [occasion, setOccasion] = useState('');
  const [filePreview, setFilePreview] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clothing'); // 'clothing', 'jewelry', or 'makeup'
  const [analysisMode, setAnalysisMode] = useState('automatic'); // 'automatic' or 'manual'
  const [manualAnalysis, setManualAnalysis] = useState({
    skin_tone: 'medium',
    body_type: 'rectangle',
    undertone: 'neutral',
    face_shape: 'oval',
    eye_color: '',
    hair_details: ''
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFilePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualAnalysis(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (analysisMode === 'automatic' && !file) {
      setError('Please select an image file for automatic analysis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (analysisMode === 'automatic') {
        // Use vision-based analysis and recommendations
        const formData = new FormData();
        formData.append('file', file);
        if (occasion) {
          formData.append('occasion', occasion);
        }
        formData.append('clothing_items', 4);  // Increased from 3
        formData.append('jewelry_items', 3);   // Increased from 2
        formData.append('makeup_items', 3);    // Added makeup items

        response = await axios.post(`${API_URL}/analyze-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Use manual inputs
        response = await axios.post(`${API_URL}/recommend`, {
          skin_tone: manualAnalysis.skin_tone,
          body_type: manualAnalysis.body_type,
          undertone: manualAnalysis.undertone,
          face_shape: manualAnalysis.face_shape,
          eye_color: manualAnalysis.eye_color || undefined,
          hair_details: manualAnalysis.hair_details || undefined,
          occasion: occasion || undefined
        }, {
          params: {
            clothing_items: 4,
            jewelry_items: 3,
            makeup_items: 3
          }
        });
      }
      
      setResults(response.data);
    } catch (err) {
      console.error('Error analyzing/recommending:', err);
      setError(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderColorPalette = () => {
    if (!results?.recommendations?.color_palette_data) return null;

    return (
      <div className="color-palette">
        <h3>Recommended Colors</h3>
        <div className="color-swatches">
          {results.recommendations.color_palette_data.map((color, index) => (
            <div
              key={index}
              className="color-swatch"
              style={{
                backgroundColor: color.hex,
                color: color.textColor,
              }}
            >
              <div className="color-name">{color.name}</div>
              <div className="color-hex">{color.hex}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderClothingItems = () => {
    const items = results?.clothing_items || [];
    if (!items.length) return <p>No clothing recommendations found.</p>;

    return (
      <div className="items-grid">
        {items.map((item, index) => (
          <div key={index} className="item-card">
            <div className="item-image">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="item-details">
              <h4>{item.title}</h4>
              <p className="item-price">{item.price}</p>
              <p className="item-brand">{item.brand} - {item.store}</p>
              <p className="item-description">{item.description}</p>
              <a href={item.url} className="item-link" target="_blank" rel="noopener noreferrer">
                View Item
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderJewelryItems = () => {
    const items = results?.jewelry_items || [];
    if (!items.length) return <p>No jewelry/accessory recommendations found.</p>;

    return (
      <div className="items-grid">
        {items.map((item, index) => (
          <div key={index} className="item-card jewelry-card">
            <div className="item-image">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="item-details">
              <h4>{item.title}</h4>
              <p className="item-price">{item.price}</p>
              <p className="item-brand">{item.brand} - {item.store}</p>
              <p className="item-description">{item.description}</p>
              <a href={item.url} className="item-link jewelry-link" target="_blank" rel="noopener noreferrer">
                View Item
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMakeupItems = () => {
    const items = results?.makeup_items || [];
    if (!items.length) return <p>No makeup recommendations found.</p>;

    return (
      <div className="items-grid">
        {items.map((item, index) => (
          <div key={index} className="item-card makeup-card">
            <div className="item-image">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="item-details">
              <h4>{item.title}</h4>
              <p className="item-price">{item.price}</p>
              <p className="item-brand">{item.brand} - {item.store}</p>
              <p className="item-description">{item.description}</p>
              <a href={item.url} className="item-link makeup-link" target="_blank" rel="noopener noreferrer">
                View Item
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAnalysisResults = () => {
    if (!results?.analysis) return null;
    
    return (
      <div className="analysis-results">
        <h3>Analysis Results</h3>
        <div className="result-card">
          <div className="result-item">
            <strong>Skin Tone:</strong> {results.analysis.skin_tone}
            <p>{results.analysis.skin_tone_explanation}</p>
          </div>
          <div className="result-item">
            <strong>Body Type:</strong> {results.analysis.body_type}
            <p>{results.analysis.body_type_explanation}</p>
          </div>
          {results.analysis.undertone && (
            <div className="result-item">
              <strong>Undertone:</strong> {results.analysis.undertone}
              {results.analysis.undertone_explanation && <p>{results.analysis.undertone_explanation}</p>}
            </div>
          )}
          {results.analysis.face_shape && (
            <div className="result-item">
              <strong>Face Shape:</strong> {results.analysis.face_shape}
              {results.analysis.face_shape_explanation && <p>{results.analysis.face_shape_explanation}</p>}
            </div>
          )}
          {results.analysis.eye_color && (
            <div className="result-item">
              <strong>Eye Color:</strong> {results.analysis.eye_color}
            </div>
          )}
          {results.analysis.hair_details && (
            <div className="result-item">
              <strong>Hair Details:</strong> {results.analysis.hair_details}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStyleRecommendations = () => {
    if (!results?.recommendations) return null;

    return (
      <div className="style-recommendations">
        <h3>Style Recommendations</h3>
        
        {results.occasion && results.recommendations.occasion_notes && (
          <div className="occasion-notes">
            <strong>Occasion: {results.occasion}</strong>
            <p>{results.recommendations.occasion_notes}</p>
          </div>
        )}
        
        <div className="recommendations-grid">
          <div className="recommendation-card">
            <h4>Recommended Styles</h4>
            <ul>
              {results.recommendations.recommended_styles.map((style, index) => (
                <li key={index}>{style}</li>
              ))}
            </ul>
          </div>
          
          <div className="recommendation-card">
            <h4>Specific Items</h4>
            <ul>
              {results.recommendations.specific_items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          
          <div className="recommendation-card">
            <h4>Items to Avoid</h4>
            <ul>
              {results.recommendations.avoid.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {results.recommendations.body_type_advice && (
            <div className="recommendation-card">
              <h4>Body Type Advice</h4>
              <p>{results.recommendations.body_type_advice}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderJewelryRecommendations = () => {
    const jewelry = results?.recommendations?.jewelry_recommendations;
    if (!jewelry) return null;

    // Handle both old (array) and new (object) formats
    if (Array.isArray(jewelry)) {
      return (
        <div className="jewelry-recommendations">
          <h3>Jewelry Recommendations</h3>
          <div className="recommendation-card">
            <ul>
              {jewelry.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    // New detailed format
    return (
      <div className="jewelry-recommendations">
        <h3>Jewelry & Accessory Recommendations</h3>
        <div className="recommendations-grid">
          {jewelry.earrings && jewelry.earrings.length > 0 && (
            <div className="recommendation-card">
              <h4>Earrings</h4>
              <ul>
                {jewelry.earrings.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.necklaces && jewelry.necklaces.length > 0 && (
            <div className="recommendation-card">
              <h4>Necklaces</h4>
              <ul>
                {jewelry.necklaces.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.bracelets_watches && jewelry.bracelets_watches.length > 0 && (
            <div className="recommendation-card">
              <h4>Bracelets & Watches</h4>
              <ul>
                {jewelry.bracelets_watches.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.rings && jewelry.rings.length > 0 && (
            <div className="recommendation-card">
              <h4>Rings</h4>
              <ul>
                {jewelry.rings.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.hair_accessories && jewelry.hair_accessories.length > 0 && (
            <div className="recommendation-card">
              <h4>Hair Accessories</h4>
              <ul>
                {jewelry.hair_accessories.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.scarves_wraps && jewelry.scarves_wraps.length > 0 && (
            <div className="recommendation-card">
              <h4>Scarves & Wraps</h4>
              <ul>
                {jewelry.scarves_wraps.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.bags_purses && jewelry.bags_purses.length > 0 && (
            <div className="recommendation-card">
              <h4>Bags & Purses</h4>
              <ul>
                {jewelry.bags_purses.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jewelry.other_accessories && jewelry.other_accessories.length > 0 && (
            <div className="recommendation-card">
              <h4>Other Accessories</h4>
              <ul>
                {jewelry.other_accessories.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {jewelry.metal_preference && (
          <div className="metal-preference">
            <h4>Metal Preference</h4>
            <p>{jewelry.metal_preference}</p>
          </div>
        )}

        {jewelry.gemstone_preference && (
          <div className="gemstone-preference">
            <h4>Gemstone Preference</h4>
            <p>{jewelry.gemstone_preference}</p>
          </div>
        )}
      </div>
    );
  };

  const renderMakeupRecommendations = () => {
    const makeup = results?.recommendations?.makeup_recommendations;
    if (!makeup) return null;

    return (
      <div className="makeup-recommendations">
        <h3>Makeup Recommendations</h3>
        <div className="recommendations-grid">
          {makeup.foundation_concealer && makeup.foundation_concealer.length > 0 && (
            <div className="recommendation-card">
              <h4>Foundation & Concealer</h4>
              <ul>
                {makeup.foundation_concealer.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.blush && makeup.blush.length > 0 && (
            <div className="recommendation-card">
              <h4>Blush</h4>
              <ul>
                {makeup.blush.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.bronzer_contour && makeup.bronzer_contour.length > 0 && (
            <div className="recommendation-card">
              <h4>Bronzer & Contour</h4>
              <ul>
                {makeup.bronzer_contour.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.eyeshadow && makeup.eyeshadow.length > 0 && (
            <div className="recommendation-card">
              <h4>Eyeshadow</h4>
              <ul>
                {makeup.eyeshadow.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.eyeliner && makeup.eyeliner.length > 0 && (
            <div className="recommendation-card">
              <h4>Eyeliner</h4>
              <ul>
                {makeup.eyeliner.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.mascara && makeup.mascara.length > 0 && (
            <div className="recommendation-card">
              <h4>Mascara</h4>
              <ul>
                {makeup.mascara.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.lips && makeup.lips.length > 0 && (
            <div className="recommendation-card">
              <h4>Lips</h4>
              <ul>
                {makeup.lips.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {makeup.techniques && makeup.techniques.length > 0 && (
            <div className="recommendation-card">
              <h4>Makeup Techniques</h4>
              <ul>
                {makeup.techniques.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {makeup.occasion_notes && (
          <div className="occasion-notes">
            <h4>Occasion Notes</h4>
            <p>{makeup.occasion_notes}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fashion-recommendation-container">
      <h1>Personal Style Assistant <span className="vision-badge">Vision AI</span></h1>
      
      <div className="analysis-mode-selector">
        <button 
          className={`mode-button ${analysisMode === 'automatic' ? 'active' : ''}`}
          onClick={() => setAnalysisMode('automatic')}
        >
          Automatic Analysis (Image Upload)
        </button>
        <button 
          className={`mode-button ${analysisMode === 'manual' ? 'active' : ''}`}
          onClick={() => setAnalysisMode('manual')}
        >
          Manual Input
        </button>
      </div>
      
      <div className="upload-section">
        <form onSubmit={handleSubmit}>
          {analysisMode === 'automatic' ? (
            <div className="automatic-analysis">
              <div className="file-input-container">
                <label className="file-input-label">
                  <span>Upload a full-body photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                </label>
                
                {filePreview && (
                  <div className="file-preview">
                    <img src={filePreview} alt="Preview" />
                  </div>
                )}
              </div>
              <div className="file-instructions">
                <p>For best results, upload a clear full-body photo in good lighting.</p>
              </div>
            </div>
          ) : (
            <div className="manual-analysis">
              <div className="input-grid">
                <div className="input-group">
                  <label>
                    Skin Tone:
                    <select 
                      name="skin_tone" 
                      value={manualAnalysis.skin_tone}
                      onChange={handleManualChange}
                    >
                      <option value="very fair">Very Fair</option>
                      <option value="fair">Fair</option>
                      <option value="medium">Medium</option>
                      <option value="olive">Olive</option>
                      <option value="brown">Brown</option>
                      <option value="dark brown">Dark Brown</option>
                    </select>
                  </label>
                </div>
                
                <div className="input-group">
                  <label>
                    Undertone:
                    <select 
                      name="undertone" 
                      value={manualAnalysis.undertone}
                      onChange={handleManualChange}
                    >
                      <option value="warm">Warm</option>
                      <option value="cool">Cool</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </label>
                </div>
                
                <div className="input-group">
                  <label>
                    Body Type:
                    <select 
                      name="body_type" 
                      value={manualAnalysis.body_type}
                      onChange={handleManualChange}
                    >
                      <option value="hourglass">Hourglass</option>
                      <option value="pear">Pear</option>
                      <option value="apple">Apple</option>
                      <option value="rectangle">Rectangle</option>
                      <option value="inverted triangle">Inverted Triangle</option>
                    </select>
                  </label>
                </div>
                
                <div className="input-group">
                  <label>
                    Face Shape:
                    <select 
                      name="face_shape" 
                      value={manualAnalysis.face_shape}
                      onChange={handleManualChange}
                    >
                      <option value="oval">Oval</option>
                      <option value="round">Round</option>
                      <option value="square">Square</option>
                      <option value="heart">Heart</option>
                      <option value="diamond">Diamond</option>
                      <option value="rectangular">Rectangular</option>
                    </select>
                  </label>
                </div>
                
                <div className="input-group">
                  <label>
                    Eye Color (optional):
                    <input
                      type="text"
                      name="eye_color"
                      value={manualAnalysis.eye_color}
                      onChange={handleManualChange}
                      placeholder="e.g., Brown, Blue, Green"
                    />
                  </label>
                </div>
                
                <div className="input-group">
                  <label>
                    Hair Details (optional):
                    <input
                      type="text"
                      name="hair_details"
                      value={manualAnalysis.hair_details}
                      onChange={handleManualChange}
                      placeholder="e.g., Brown curly hair"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
          
          <div className="occasion-input">
            <label>
              Occasion (optional):
              <input
                type="text"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder="e.g., Wedding, Job Interview, Casual Outing"
              />
            </label>
          </div>
          
          <button 
            type="submit" 
            className="analyze-button"
            disabled={loading || (analysisMode === 'automatic' && !file)}
          >
            {loading ? 'Analyzing...' : 'Get Style Recommendations'}
          </button>
        </form>
        
        {error && <div className="error-message">{error}</div>}
      </div>
      
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>
            {analysisMode === 'automatic' 
              ? 'Analyzing your image and generating personalized recommendations...' 
              : 'Generating personalized recommendations...'}
          </p>
        </div>
      )}
      
      {results && !loading && (
        <div className="results-container">
          {renderAnalysisResults()}
          {renderColorPalette()}
          {renderStyleRecommendations()}
          {renderJewelryRecommendations()}
          {renderMakeupRecommendations()}
          
          <div className="product-recommendations">
            <h3>Product Recommendations</h3>
            
            <div className="tab-selector">
              <button
                className={`tab-button ${activeTab === 'clothing' ? 'active' : ''}`}
                onClick={() => setActiveTab('clothing')}
              >
                Clothing
              </button>
              <button
                className={`tab-button ${activeTab === 'jewelry' ? 'active' : ''}`}
                onClick={() => setActiveTab('jewelry')}
              >
                Jewelry & Accessories
              </button>
              <button
                className={`tab-button ${activeTab === 'makeup' ? 'active' : ''}`}
                onClick={() => setActiveTab('makeup')}
              >
                Makeup
              </button>
            </div>
            
            <div className="tab-content">
              {activeTab === 'clothing' ? renderClothingItems() : 
               activeTab === 'jewelry' ? renderJewelryItems() : 
               renderMakeupItems()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FashionRecommendation;